import React, { useEffect, useState } from "react";
import { evaluateContextPersisted } from "@depmap/api";
import { showInfoModal } from "@depmap/common-components";
import SliceTable from "@depmap/slice-table";
import { isV2Context } from "../../utils/context";
import { fetchContext } from "../../utils/context-storage";
import { getDimensionTypeLabel, uncapitalize } from "../../utils/misc";
import { pluralize } from "../../utils/dimensionHelpers";
import { convertContextV1toV2 } from "../../utils/context-converter";
import { usePlotlyLoader } from "../../contexts/PlotlyLoaderContext";
import styles from "../../styles/ContextManager.scss";

function DownloadTable({
  contextHash,
  dimension_type,
  contextName,
  PlotlyLoader,
}: {
  contextName: string;
  dimension_type: string;
  contextHash: string;
  PlotlyLoader: ReturnType<typeof usePlotlyLoader>;
}) {
  const [contextIds, setContextIds] = useState<Set<string>>();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      let context = await fetchContext(contextHash);

      if (!isV2Context(context)) {
        context = await convertContextV1toV2(context);
      }

      const { ids, num_candidates } = await evaluateContextPersisted(context);
      setContextIds(new Set(ids));
      setTotal(num_candidates);
    })();
  }, [contextHash]);

  const entity = getDimensionTypeLabel(dimension_type);
  const entities = uncapitalize(pluralize(entity));

  return (
    <SliceTable
      PlotlyLoader={PlotlyLoader}
      index_type_name={dimension_type}
      downloadFilename={contextName}
      // The context's ids are resolved before the table mounts, so they scope
      // the fetch rather than filtering it afterwards. The predicate stays for
      // the slices Breadbox can't subset (matrix slices, reindex_through
      // chains), which still come back whole.
      rowIds={contextIds ?? undefined}
      implicitFilter={({ id }) => Boolean(contextIds?.has(id))}
      isLoading={!contextIds}
      renderCustomControls={() => {
        if (!contextIds) {
          return null;
        }

        return (
          <div>
            Showing <b>{contextIds.size.toLocaleString()}</b> matches of{" "}
            {total.toLocaleString()} {entities}
          </div>
        );
      }}
    />
  );
}

function showDownloadContextModal(
  contextName: string,
  dimension_type: string,
  contextHash: string,
  PlotlyLoader: ReturnType<typeof usePlotlyLoader>
) {
  showInfoModal({
    title: `Download context '${contextName}'`,
    modalProps: { className: "foo", bsSize: "lg" },
    content: (
      <div className={styles.downloadContextTable}>
        <DownloadTable
          contextName={contextName}
          dimension_type={dimension_type}
          contextHash={contextHash}
          PlotlyLoader={PlotlyLoader}
        />
      </div>
    ),
  });
}

export default showDownloadContextModal;
